import { Skeleton } from "@/components/ui/skeleton";

/**
 * Placeholder for `HcbbSearchResults` while the cost details are in flight.
 * It mirrors that layout block for block — highlight card at a quarter width,
 * tab strip, then a stack of result rows — so the real results drop in without
 * the page reflowing underneath the member.
 */
export const HcbbSearchResultsSkeleton = ({
  rowCount = 3,
}: {
  rowCount?: number;
}) => (
  <section
    // The whole block is decorative: `HcbbSearchResults` owns the live region
    // that announces "Loading cost estimates", so nothing is announced twice.
    aria-hidden="true"
    className="flex gap-4 space-y-4"
  >
    <div className="w-1/4">
      <HighlightSkeleton />
    </div>
    <div className="flex-1 space-y-2">
      <div className="flex h-9 w-fit items-center gap-2 rounded-lg bg-gray-200 p-1">
        <Skeleton className="h-7 w-28 bg-gray-300" />
        <Skeleton className="h-7 w-28 bg-gray-300" />
      </div>
      <ul className="space-y-2">
        {Array.from({ length: rowCount }, (_, index) => (
          <li key={index}>
            <HcbbResultRowSkeleton />
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const HighlightSkeleton = () => (
  <div className="border-1 space-y-4 rounded-lg border p-4 shadow-md">
    <Skeleton className="h-5 w-3/4 bg-gray-200" />

    <div className="space-y-2">
      <Skeleton className="h-4 w-40 bg-gray-200" />
      <Skeleton className="h-6 w-24 bg-gray-200" />
    </div>

    {/* Stands in for the price meter and its low/high labels. */}
    <div className="space-y-2">
      <Skeleton className="h-2 w-full rounded-full bg-gray-200" />
      <div className="flex justify-between">
        <Skeleton className="h-3 w-16 bg-gray-200" />
        <Skeleton className="h-3 w-16 bg-gray-200" />
      </div>
    </div>

    <div className="space-y-2">
      <Skeleton className="h-4 w-48 bg-gray-200" />
      <Skeleton className="h-6 w-24 bg-gray-200" />
    </div>

    <hr />
    <div className="space-y-2">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex items-center justify-between">
          <Skeleton className="h-4 w-32 bg-gray-200" />
          <Skeleton className="h-4 w-14 bg-gray-200" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * One facility/provider card — both rows share the same two-column shape.
 *
 * Exported because the providers tab appends these below rows that are
 * already on screen while its next batch loads. `aria-hidden` travels with the
 * row so it stays decorative wherever it is used — the list's count line is
 * what announces the load.
 */
export const HcbbResultRowSkeleton = () => (
  <div
    aria-hidden="true"
    className="border-1 border-gray rounded-lg border shadow-md"
  >
    <div className="flex items-center justify-between p-2">
      <Skeleton className="h-5 w-52 bg-gray-200" />
      <Skeleton className="h-6 w-24 rounded-full bg-gray-200" />
    </div>
    <hr />
    <div className="flex gap-4 p-4">
      <div className="flex-1 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-full bg-gray-200" />
          <Skeleton className="h-4 w-24 bg-gray-200" />
        </div>
        <div className="flex gap-6">
          <Skeleton className="h-4 w-48 bg-gray-200" />
          <Skeleton className="h-4 w-40 bg-gray-200" />
        </div>
      </div>
      <div className="flex-1 space-y-4">
        <div className="flex justify-end">
          <Skeleton className="h-6 w-56 bg-gray-200" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-4 w-64 bg-gray-200" />
        </div>
        <div className="flex justify-end gap-2">
          <Skeleton className="size-9 bg-gray-200" />
          <Skeleton className="size-9 bg-gray-200" />
          <Skeleton className="h-9 w-44 bg-gray-200" />
        </div>
      </div>
    </div>
  </div>
);
